import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Rocket, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import { DeployLiveConfirmDialog } from '@/Domain/components/KnowledgeBase/SonyDocumentSheet/components/DeployLiveConfirmDialog';
import { RunPipelineConfirmDialog } from '@/Domain/components/KnowledgeBase/SonyDocumentSheet/components/RunPipelineConfirmDialog';
import { SonyArticleEditor } from '@/Domain/components/KnowledgeBase/SonyDocumentSheet/components/SonyArticleEditor';
import { SonyDocumentFieldsForm } from '@/Domain/components/KnowledgeBase/SonyDocumentSheet/components/SonyDocumentFieldsForm';
import { SonyDocumentHistory } from '@/Domain/components/KnowledgeBase/SonyDocumentSheet/components/SonyDocumentHistory';
import { SonyDocumentPayload } from '@/Domain/components/KnowledgeBase/SonyDocumentSheet/components/SonyDocumentPayload';
import { SonyDocumentStatusBadge } from '@/Domain/components/KnowledgeBase/SonyDocumentStatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  SonyAdminDocumentDetailDto,
  SonyDocumentStatusEnum,
} from '@/UI/KnowledgeBase/SonyDocuments/SonyDocuments.dto';

const SHEET_ANIMATION_DURATION = 320;

// Mirror of DEPLOYABLE_SONY_DOCUMENT_STATUSES in sony_admin_tasks.py — keep in sync.
const DEPLOYABLE_STATUSES: readonly SonyDocumentStatusEnum[] = [
  SonyDocumentStatusEnum.APP_ARTICLES_CREATED,
  SonyDocumentStatusEnum.DEPLOYED,
];

export const SonyDocumentSheet = () => {
  const { t } = useTranslation('sony_admin');
  const navigate = useNavigate();
  const { scenario, externalId = '' } = useParams<{ scenario?: string; externalId?: string }>();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [pipelineDialogOpen, setPipelineDialogOpen] = useState(false);

  const detailQuery = useQuery<SonyAdminDocumentDetailDto>({
    queryKey: [QueryKey.sonyAdmin.document, externalId],
    enabled: Boolean(externalId),
    gcTime: 0,
  });

  const runPipelineMutation = useMutation<unknown, unknown, string>({
    mutationKey: [QueryKey.sonyAdmin.runPipeline],
  });
  const deployLiveMutation = useMutation<unknown, unknown, string>({
    mutationKey: [QueryKey.sonyAdmin.deployLive],
  });

  const navigateToSonyDocuments = useCallback(() => {
    const path = scenario
      ? `/i/${scenario}/${IndexPath.knowledgeBase.sony}`
      : `/i/${IndexPath.knowledgeBase.sony}`;
    navigate(path);
  }, [navigate, scenario]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) return;
      setOpen(false);
      setTimeout(() => {
        navigateToSonyDocuments();
      }, SHEET_ANIMATION_DURATION);
    },
    [navigateToSonyDocuments],
  );

  const detail = detailQuery.data;
  const currentRow = detail?.staged ?? detail?.rows[0];
  const failMessage = detail?.rows[0]?.fail_message;
  const pipelineInProgress = Boolean(detail?.pipeline_in_progress);
  const retrainInProgress = Boolean(detail?.retrain_in_progress);
  const deployInProgress = Boolean(detail?.deploy_in_progress);
  const serverBusy = pipelineInProgress || retrainInProgress || deployInProgress;
  const isBusy = serverBusy || runPipelineMutation.isPending || deployLiveMutation.isPending;
  const canDeploy = Boolean(detail?.rows.some((row) => DEPLOYABLE_STATUSES.includes(row.status)));

  const wasServerBusyRef = useRef(false);
  useEffect(() => {
    if (wasServerBusyRef.current && !serverBusy) {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.documents] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.sonyAdmin.document, externalId] });
    }
    wasServerBusyRef.current = serverBusy;
  }, [serverBusy, queryClient, externalId]);

  const stats = detail
    ? ([
        { key: 'preview_chunks', value: detail.preview_chunk_count },
        { key: 'live_chunks', value: detail.live_chunk_count },
        { key: 'pending_embeds', value: detail.pending_embed_count },
        { key: 'deploy_id', value: detail.current_deploy_id ?? '' },
      ] as const)
    : [];

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="!max-w-[100vw] xl:!max-w-[70%] 2xl:!max-w-4xl w-full">
        <SheetHeader className="!flex-row !items-center !justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <SheetTitle className="truncate font-mono" data-testid="sony-sheet-title">
              {externalId}
            </SheetTitle>
            {currentRow && <SonyDocumentStatusBadge status={currentRow.status} />}
            {detail?.staged && (
              <Badge variant="warning" data-testid="sony-staged-badge">
                <span className="whitespace-nowrap text-xs">{t('sheet.staged_badge')}</span>
              </Badge>
            )}
          </div>
          <SheetDescription className="sr-only">{t('title')}</SheetDescription>
          <IconButton
            variant="secondary"
            onClick={() => handleOpenChange(false)}
            aria-label={t('sheet.close')}
          >
            <X className="size-4" />
          </IconButton>
        </SheetHeader>

        <div className="custom-scroll min-h-0 flex-1 space-y-8 overflow-y-auto pr-1">
          {detailQuery.isLoading || !detail || !currentRow ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <>
              {failMessage && (
                <Alert variant="destructive" data-testid="sony-fail-alert">
                  <TriangleAlert className="size-4" />
                  <AlertTitle>{t('sheet.fail_title')}</AlertTitle>
                  <AlertDescription className="break-words">{failMessage}</AlertDescription>
                </Alert>
              )}
              {retrainInProgress && (
                <Alert variant="warning" data-testid="sony-retrain-alert">
                  <AlertDescription>{t('sheet.retrain_running')}</AlertDescription>
                </Alert>
              )}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {stats.map(({ key, value }) => (
                  <div key={key} className="rounded-xl border border-border bg-muted/50 p-3">
                    <div className="text-xs text-muted-foreground">{t(`sheet.stats.${key}`)}</div>
                    <div className="text-sm font-semibold tabular-nums text-foreground">
                      {value}
                    </div>
                  </div>
                ))}
              </div>
              <SonyDocumentFieldsForm
                externalId={externalId}
                sonyDocument={currentRow}
                disabled={isBusy}
              />
              <SonyDocumentPayload externalId={externalId} />
              <SonyArticleEditor
                externalId={externalId}
                article={detail.article}
                disabled={isBusy}
              />
              <SonyDocumentHistory rows={detail.rows} historyTotal={detail.history_total} />
            </>
          )}
        </div>

        <SheetFooter>
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="danger"
              size="sm"
              onClick={() => setDeployDialogOpen(true)}
              loading={deployLiveMutation.isPending || deployInProgress}
              disabled={isBusy || !canDeploy}
              data-testid="sony-deploy-live-button"
            >
              <Rocket className="size-4" />
              {t('sheet.deploy_live')}
            </Button>
            <Button
              size="sm"
              onClick={() => setPipelineDialogOpen(true)}
              loading={runPipelineMutation.isPending || pipelineInProgress}
              disabled={isBusy}
              data-testid="sony-run-pipeline-button"
            >
              <Play className="size-4" />
              {pipelineInProgress ? t('sheet.pipeline_running') : t('sheet.run_pipeline')}
            </Button>
          </div>
        </SheetFooter>

        <DeployLiveConfirmDialog
          open={deployDialogOpen}
          onOpenChange={setDeployDialogOpen}
          isLoading={deployLiveMutation.isPending}
          onConfirm={() =>
            deployLiveMutation.mutate(externalId, {
              onSettled: () => setDeployDialogOpen(false),
            })
          }
        />

        <RunPipelineConfirmDialog
          open={pipelineDialogOpen}
          onOpenChange={setPipelineDialogOpen}
          isLoading={runPipelineMutation.isPending}
          onConfirm={() =>
            runPipelineMutation.mutate(externalId, {
              onSettled: () => setPipelineDialogOpen(false),
            })
          }
        />
      </SheetContent>
    </Sheet>
  );
};
